import type {
  FetchMatcher,
  JsonApiDoc,
  JsonApiResource,
  MatcherFixture,
} from 'tio-data/demo/types';
import type { DemoFixtureIdentity } from 'tio-employee/demo/types';

/**
 * Fixtures for the "Secure Your Future" (SYF / SECURE 2.0 employer match)
 * demo: an ENROLLED participant mid-benefit-year — match summary with
 * certified + qualified + unqualified payments, a pending loan payment
 * awaiting verification, and the 401(k) insights column.
 *
 * TWO personas, both real states a live user can be in:
 * - Every persona except First Login gets `enrollmentState: COMPLETE`, so
 *   `/syf` routes straight to the dashboard and the enrollment routes bounce
 *   away on their `isEnrolled` guards — those mutations stay unreachable.
 * - First Login gets `NOT_STARTED`, which is what a brand-new employee is, so
 *   the rep walks the SHIPPED enrollment screens. No UI is invented and no
 *   state is faked: the guards only read `isEnrolled`, both enrollment PATCHes
 *   answer `head 204` in production (so the inert demo response is
 *   indistinguishable), and the completion panel is local component state.
 *
 * Seams (the SYF tree is ALL WarpDrive builders — no raw fetch/ajax):
 * - `match-payment` + `account-transaction` queries carry no SkipCache →
 *   fetch seam → the FetchMatchers below.
 * - `/match-participants/:id/insights` (GET) and `/match-payments/certify`
 *   (PATCH) are SkipCache builders → the demo request handler → the
 *   builder-kind matchers below.
 * - The participant/plan graph itself rides the employee reads
 *   (`/employees/:id?include=match-participant.*` and `/me`) — sideloaded by
 *   identity-fixtures via {@link buildMatchParticipantGraph}, gated on the
 *   employerMatch product.
 *
 * UNITS: every SYF money field is CENTS (`match-participant.ext` and the
 *   dashboard formatters divide by 100); the insights payload is CENTS AS
 *   STRINGS (`FloatAsString` — formatCentsToDollars parseFloats them).
 */

export const MATCH_PARTICIPANT_ID = 'demo-syf-participant-1';
export const MATCH_PLAN_ID = 'demo-syf-plan-1';
const SYF_EMAIL_ID = 'demo-syf-email-1';
// Reuses the identity loan graph's institution id/name so the payments tables
// and the person's linked-account table narrate the same servicer.
const FI_ID = 'demo-person-fi-1';
const FI_NAME = 'MOHELA';

// The benefit year the dashboard picks: the first year whose
// final_certification_date is still in the future (dashboard.ts picks it,
// match-summary indexes metadata.benefit_years[year] UNGUARDED — the entry
// must exist or the summary throws). Keep the whole payment story in the
// current benefit year so the fixture cannot expire after a fixed cutoff.
export const DEMO_BENEFIT_YEAR = new Date().getFullYear().toString();
// Date-only: the match summary renders this string RAW (no formatter), and the
// dashboard's year-picking compares `new Date(...)` > now — both are happy
// with a plain date, while a full ISO timestamp reads as machine noise.
const FINAL_CERTIFICATION_DATE = `${Number(DEMO_BENEFIT_YEAR) + 1}-03-31`;
const BENEFIT_START_DATE = `${DEMO_BENEFIT_YEAR}-01-01`;

const demoEmail = (identity: DemoFixtureIdentity) =>
  `${identity.firstName}.${identity.lastName}`.toLowerCase().replace(/\s+/g, '-') + '@example.com';

/**
 * The enrolled participant + plan + personal email, shaped for sideloading
 * into BOTH employee graphs (`/employees/:id` detail read and the `/me`
 * bootstrap) — the `/syf` parent route and the dashboard's `beforeModel` gate
 * read them from different graphs, and each resolves async:false.
 */
export function buildMatchParticipantGraph(
  identity: DemoFixtureIdentity,
  options: { enrolled?: boolean } = {}
): {
  participantRef: { type: string; id: string };
  included: JsonApiResource[];
} {
  // `enrolled: false` serves a NOT_STARTED participant, which is what puts the
  // rep on the SHIPPED enrollment screens (/syf/enrollment →
  // /syf/account-confirmation → the "Enrollment Complete" panel) instead of the
  // dashboard. Nothing is faked to make that work: every guard on those routes
  // only asks `isEnrolled`, the two enrollment PATCHes answer `head 204` in
  // production too (so demo's inert 204 is indistinguishable from success), and
  // the completion panel is local component state. The participant must still
  // EXIST or the /syf parent route bounces the whole tree to the dashboard.
  const enrolled = options.enrolled ?? true;
  const participant: JsonApiResource = {
    type: 'match-participant',
    id: MATCH_PARTICIPANT_ID,
    attributes: {
      // isEnrolled (match-participant.ext) is the single gate deciding whether
      // /syf lands on the rich dashboard or the enrollment flow.
      enrollmentState: enrolled
        ? 'SYF.EnrollmentState.COMPLETE'
        : 'SYF.EnrollmentState.NOT_STARTED',
      state: enrolled ? 'SYF.ParticipantState.ACTIVE' : 'SYF.ParticipantState.CREATED',
      // Both non-null ⇒ hasDataForInsights ⇒ the insights request fires and
      // the 401(k) column renders. CENTS: $87,320 salary / $412.18 monthly.
      // Deliberately not round — even amounts read as fake in a demo.
      reportedSalary: 8_732_000,
      reportedMonthlyPayment: 41_218,
      benefitStartDate: BENEFIT_START_DATE,
      // enrollmentDate derives from the first transition into a completed state;
      // a not-yet-enrolled participant has no such transition.
      transitionLog: !enrolled
        ? []
        : [
            {
              event: 'complete_enrollment',
              from: 'SYF.EnrollmentState.DRAFT',
              to: 'SYF.EnrollmentState.COMPLETE',
              timestamp: `${DEMO_BENEFIT_YEAR}-01-15T15:00:00Z`,
              reason: null,
            },
          ],
    },
    relationships: {
      matchPlan: { data: { type: 'match-plan', id: MATCH_PLAN_ID } },
      // A present emailAddress pre-empts personal-email.gts's construct-time
      // `createRecord('email-address')` (mutations are blocked in demo).
      emailAddress: { data: { type: 'email-address', id: SYF_EMAIL_ID } },
    },
  };

  const plan: JsonApiResource = {
    type: 'match-plan',
    id: MATCH_PLAN_ID,
    attributes: {
      planName: `${identity.employerName} SECURE 2.0 Match`,
      // Gates the whole insights column on the dashboard.
      showInsights: true,
      qualifyingIncome: 'SYF.QualifyingIncome.PRETAX_ANNUAL',
      benefitStartDate: BENEFIT_START_DATE,
      // Plain string (matchStatement may also be EditorJS JSON; plain is safest).
      matchStatement: `${identity.employerName} contributes to your 401(k) for every qualifying student loan payment you make — up to 5% of your eligible salary each year.`,
      matchFrequencyDescription:
        'Matching contributions are deposited annually after certification.',
      verifyAccountStatement:
        'Confirm each linked loan account belongs to you so its payments can qualify for matching.',
      // Sorted/iterated unguarded by the FAQ list — must be an array.
      faqs: [],
      metadata: {
        benefit_years: {
          [DEMO_BENEFIT_YEAR]: {
            benefit_year: Number(DEMO_BENEFIT_YEAR),
            final_certification_date: FINAL_CERTIFICATION_DATE,
          },
        },
      },
    },
  };

  const email: JsonApiResource = {
    type: 'email-address',
    id: SYF_EMAIL_ID,
    attributes: {
      email: demoEmail(identity),
      contactType: 'PERSONAL',
      isActive: true,
    },
  };

  return {
    participantRef: { type: 'match-participant', id: MATCH_PARTICIPANT_ID },
    included: [participant, plan, email],
  };
}

// --- Payments story (all CENTS) ----------------------------------------------
// Jan–May of the current benefit year: one below-threshold payment ($185.40)
// that did NOT qualify, three qualifying $412.18 payments (two already
// certified, one awaiting the participant's certification — lights the
// "Certify" CTA), and one loan payment still pending verification (fills the
// unverified table). The qualifying amounts are EQUAL to each other on purpose
// — a fixed auto-debit really is the same every month — but not a round number.

interface DemoTransaction {
  id: string;
  amountCents: number;
  date: string;
  verificationState: string;
  rejectionNote?: string;
}

const TRANSACTIONS: DemoTransaction[] = [
  {
    id: 'demo-syf-txn-1',
    amountCents: 18_540,
    date: `${DEMO_BENEFIT_YEAR}-01-20`,
    verificationState: 'TransactionVerificationState.VERIFIED',
  },
  {
    id: 'demo-syf-txn-2',
    amountCents: 41_218,
    date: `${DEMO_BENEFIT_YEAR}-02-18`,
    verificationState: 'TransactionVerificationState.VERIFIED',
  },
  {
    id: 'demo-syf-txn-3',
    amountCents: 41_218,
    date: `${DEMO_BENEFIT_YEAR}-03-18`,
    verificationState: 'TransactionVerificationState.VERIFIED',
  },
  {
    id: 'demo-syf-txn-4',
    amountCents: 41_218,
    date: `${DEMO_BENEFIT_YEAR}-04-17`,
    verificationState: 'TransactionVerificationState.VERIFIED',
  },
  {
    id: 'demo-syf-txn-5',
    amountCents: 41_218,
    date: `${DEMO_BENEFIT_YEAR}-05-18`,
    verificationState: 'TransactionVerificationState.PENDING_VERIFICATION',
  },
];

const FI_RESOURCE: JsonApiResource = {
  type: 'financial-institution',
  id: FI_ID,
  attributes: { name: FI_NAME, isActive: true },
};

function transactionResource(txn: DemoTransaction): JsonApiResource {
  return {
    type: 'account-transaction',
    id: txn.id,
    attributes: {
      amount: txn.amountCents,
      transactionDate: txn.date,
      transactionYear: Number(DEMO_BENEFIT_YEAR),
      createdAt: `${txn.date}T12:00:00Z`,
      verificationState: txn.verificationState,
      transactionType: 'TransactionType.PAYMENT',
      category: 'TransactionCategory.DEBIT',
      transitionLog: txn.rejectionNote
        ? [
            {
              to: 'TransactionVerificationState.REJECTED',
              reason: { note: txn.rejectionNote },
            },
          ]
        : [],
    },
    relationships: {
      financialInstitution: { data: { type: 'financial-institution', id: FI_ID } },
    },
  };
}

interface DemoMatchPayment {
  id: string;
  txn: DemoTransaction;
  qualificationState: string;
  certified: boolean;
}

const MATCH_PAYMENTS: DemoMatchPayment[] = [
  {
    // Below the plan's qualifying threshold — renders in the ineligible table,
    // showing the qualification logic is transparent.
    id: 'demo-syf-payment-1',
    txn: TRANSACTIONS[0]!,
    qualificationState: 'SYF.PaymentQualificationState.UNQUALIFIED',
    certified: false,
  },
  {
    id: 'demo-syf-payment-2',
    txn: TRANSACTIONS[1]!,
    qualificationState: 'SYF.PaymentQualificationState.QUALIFIED',
    certified: true,
  },
  {
    id: 'demo-syf-payment-3',
    txn: TRANSACTIONS[2]!,
    qualificationState: 'SYF.PaymentQualificationState.QUALIFIED',
    certified: true,
  },
  {
    // Qualified but not yet certified — lights the dashboard's "Certify" CTA.
    id: 'demo-syf-payment-4',
    txn: TRANSACTIONS[3]!,
    qualificationState: 'SYF.PaymentQualificationState.QUALIFIED',
    certified: false,
  },
];

function matchPaymentResource(payment: DemoMatchPayment, certified: boolean): JsonApiResource {
  return {
    type: 'match-payment',
    id: payment.id,
    attributes: {
      certified,
      qualificationState: payment.qualificationState,
      transactionAmount: payment.txn.amountCents,
      transactionDate: payment.txn.date,
      transactionYear: Number(DEMO_BENEFIT_YEAR),
    },
    relationships: {
      // The qualified/ineligible tables render rows off accountTransaction —
      // a payment without one silently vanishes.
      accountTransaction: { data: { type: 'account-transaction', id: payment.txn.id } },
      matchParticipant: { data: { type: 'match-participant', id: MATCH_PARTICIPANT_ID } },
    },
  };
}

const MATCH_PAYMENT_COLLECTION: JsonApiDoc = {
  data: MATCH_PAYMENTS.map((payment) => matchPaymentResource(payment, payment.certified)),
  included: [...MATCH_PAYMENTS.map((payment) => transactionResource(payment.txn)), FI_RESOURCE],
};

const ACCOUNT_TRANSACTION_COLLECTION: JsonApiDoc = {
  data: TRANSACTIONS.map(transactionResource),
  included: [FI_RESOURCE],
};

// Certify response: the store pushes this doc, so the uncertified qualified
// payment flips to certified IN-SESSION (a real visible state change with no
// network) and the modal resolves cleanly instead of dying on an inert 204.
const CERTIFY_RESPONSE: JsonApiDoc = {
  data: MATCH_PAYMENTS.filter((payment) => !payment.certified && isQualified(payment)).map(
    (payment) => matchPaymentResource(payment, true)
  ),
};

function isQualified(payment: DemoMatchPayment): boolean {
  return payment.qualificationState === 'SYF.PaymentQualificationState.QUALIFIED';
}

// 401(k) insights — CENTS AS STRINGS (FloatAsString on the wire; the
// formatters parseFloat then /100). remainingMonthsInYear is a number.
// maxPossibleMatch is 5% of the reported salary (matching the plan copy) and
// yearlyMatch + matchDelta reconcile to it — a prospect who adds these up on
// screen gets the right answer.
// defermentDeltaPerMonth > 0 drives the "you could earn more" coaching copy.
const INSIGHTS_CONTENT = {
  data: {
    maxPossibleMatch: '436600',
    yearlyMatch: '82436',
    matchDelta: '354164',
    defermentDelta: '152580',
    defermentDeltaPerMonth: '12715',
    remainingMonthsInYear: 5,
  },
};

/** Request-handler (SkipCache builder) matchers for the SYF dashboard. */
export function buildEmployerMatchMatchers(): MatcherFixture[] {
  return [
    {
      matcher: {
        kind: 'builder',
        method: 'GET',
        urlMatches: (url) => /\/match-participants\/[^/]+\/insights(?:\?|$)/.test(url),
      },
      content: INSIGHTS_CONTENT,
    },
    {
      matcher: { kind: 'builder', method: 'PATCH', urlIncludes: '/match-payments/certify' },
      content: CERTIFY_RESPONSE,
    },
  ];
}

/**
 * Fetch-seam matchers for the plain (no-SkipCache) builder queries.
 *
 * `identity` + `enrolled` are only needed for the participant READ: both
 * enrollment helpers (beginMatchParticipantEnrollment / activateMatchParticipant)
 * follow their PATCH with `reloadRecord`, i.e. `GET /match-participants/:id`.
 * Unscripted that lands on the network-free block (`data: null`), which is
 * recorded as a fixture gap and would leave the flow depending on the reload
 * being ignored. Serving the SAME participant keeps the enrollment path fully
 * scripted and deterministic.
 */
export function buildEmployerMatchFetchMatchers(
  identity?: DemoFixtureIdentity,
  enrolled = true
): FetchMatcher[] {
  const participantDoc = identity
    ? (() => {
        const graph = buildMatchParticipantGraph(identity, { enrolled });
        const participant = graph.included.find((r) => r.type === 'match-participant');
        return {
          data: participant,
          included: graph.included.filter((r) => r.type !== 'match-participant'),
        };
      })()
    : null;

  return [
    // Participant single read (the post-PATCH reloadRecord). Must NOT shadow
    // the `/insights` subroute, which is a SkipCache builder served by the
    // request handler.
    ...(participantDoc
      ? [
          {
            urlMatches: (url: string) =>
              /\/match-participants\/[^/?]+(\?|$)/.test(url) && !url.includes('/insights'),
            method: 'GET',
            json: participantDoc,
          } satisfies FetchMatcher,
        ]
      : []),
    {
      urlMatches: (url) => /\/match-payments(\?|$)/.test(url),
      method: 'GET',
      json: MATCH_PAYMENT_COLLECTION,
    },
    {
      urlMatches: (url) => /\/account-transactions(\?|$)/.test(url),
      method: 'GET',
      json: ACCOUNT_TRANSACTION_COLLECTION,
    },
  ];
}
